/**
 * Client Sentry shim. No-op when VITE_SENTRY_DSN is unset, or when the
 * @sentry/react dep is not installed.
 *
 * Browser init happens at top of main.jsx. If Sentry is not installed
 * the app renders normally with a console.warn.
 */

let cached = null;

async function initSentry() {
  if (cached) return cached;
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    cached = { enabled: false, reason: 'no_dsn', Sentry: null };
    return cached;
  }

  try {
    // Assemble the module name at runtime so Vite's static import analyser
    // does not try to resolve @sentry/react at dev-server start. The dep is
    // optional — installing @sentry/react is only required when
    // VITE_SENTRY_DSN is set in production builds.
    const mod = '@sentry/' + 'react';
    const Sentry = await import(/* @vite-ignore */ mod);
    Sentry.init({
      dsn,
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
      release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
      tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0),
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      beforeSend(event) {
        try {
          if (event.request?.headers) {
            delete event.request.headers.authorization;
            delete event.request.headers.cookie;
          }
        } catch (_) {
          // ignore
        }
        return event;
      },
    });
    cached = { enabled: true, reason: 'ok', Sentry };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[sentry] VITE_SENTRY_DSN set but @sentry/react is not installed — observability disabled');
    cached = { enabled: false, reason: 'module_missing', Sentry: null };
  }
  return cached;
}

export async function captureClientError(err, context) {
  const { Sentry } = await initSentry();
  if (!Sentry) return;
  try {
    Sentry.withScope((scope) => {
      if (context && typeof context === 'object') {
        for (const [k, v] of Object.entries(context)) scope.setExtra(k, v);
      }
      Sentry.captureException(err);
    });
  } catch (_) {
    // ignore
  }
}

export default initSentry;
